import type { User } from 'firebase/auth';

export interface PageReviewCalloutSummary {
  id: string;
  order: number;
  priority: 'must_fix' | 'should_fix';
  label: string;
  category: string;
  currentOrGap: string;
  proposedChange: string;
  why: string;
  evidence: Array<{
    lane: 'page' | 'search_demand' | 'human_behavior' | 'ai_answers' | 'citations' | 'north_star';
    source: string;
    id: string;
    note: string;
    sampleCount?: number;
    sampleDenominator?: number;
    fanoutQuery?: string;
  }>;
}

export interface PageReviewSummary {
  id: string;
  crawl_id: string;
  page_id: string;
  page_url: string;
  html_sha256: string;
  rendered_preview_sha256?: string | null;
  version: number;
  status: 'queued' | 'running' | 'completed' | 'failed';
  submit_ready: boolean;
  assessment: null | {
    pagePurpose: string;
    buyerStage: string;
    summary: string;
    strengths: string[];
    risks: string[];
    noSupportedChangesReason?: string;
  };
  callouts: PageReviewCalloutSummary[];
  task_id: string | null;
  error: { code: string; message: string } | null;
  executor: { session_id: string | null };
  skill: { version_id: string; content_sha256: string };
  evidence_scope: {
    sampler_job_id: string;
    analytics_sources: string[];
    analytics_start_date: string | null;
    analytics_end_date: string | null;
    north_star: {
      document_id: string;
      version: number;
      content_sha256: string;
    } | null;
  };
}

export interface PageReviewQueuePage {
  id: string;
  crawl_id: string;
  url: string;
  normalized_url: string | null;
  title: string | null;
  description: string | null;
  og_image: string | null;
  word_count: number | null;
  http_status: number | null;
  seo_score: number | null;
  seo_grade: string | null;
  html_sha256: string;
  opportunity_label: string;
  search_impressions: number;
  search_clicks: number;
  citation_count: number;
  sessions: number;
  review: PageReviewSummary | null;
}

export interface PageReviewQueuePayload {
  crawlId: string | null;
  pages: PageReviewQueuePage[];
  complete: boolean;
  nextPage: PageReviewQueuePage | null;
}

type PageReviewCaptureIdentity = Pick<PageReviewSummary, 'html_sha256' | 'rendered_preview_sha256'>;

export function pageReviewCaptureMatches(
  left: PageReviewCaptureIdentity,
  right: PageReviewCaptureIdentity
) {
  return (
    left.html_sha256 === right.html_sha256 &&
    (left.rendered_preview_sha256 || null) === (right.rendered_preview_sha256 || null)
  );
}

export function mergePageReviewVersionForCapture<
  T extends PageReviewCaptureIdentity & { id: string },
>(current: T[], next: T): T[] {
  return [
    next,
    ...current.filter(item => item.id !== next.id && pageReviewCaptureMatches(item, next)),
  ];
}

export async function callPageReview<T>(
  user: User,
  action: string,
  body: Record<string, unknown>
): Promise<T> {
  const token = await user.getIdToken();
  const response = await fetch(`/api/attune/page-review/${action}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || 'Page Review request failed.');
  return payload;
}

export function pageReviewHref(input: { projectId: string; crawlId: string; pageId: string }) {
  return `/app/pages/review?projectId=${encodeURIComponent(input.projectId)}&crawlId=${encodeURIComponent(input.crawlId)}&pageId=${encodeURIComponent(input.pageId)}`;
}
